import { utcCalendarDate } from "@dailywars/core/engine/unitDefHoldover";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { DISCORD_INVITE_URL } from "#/lib/discordInvite";

export type UpdateCategory = "feature" | "balance" | "bugfix";

export type ChangelogEntry = {
  id: string;
  date: string;
  title: string;
  items: { category: UpdateCategory; text: ReactNode }[];
};

export const CHANGELOG_ENTRIES: ChangelogEntry[] = [
  {
    id: "2026-08-30",
    date: "2026-08-30",
    title: "Unit movement changes",
    items: [
      {
        category: "balance",
        text: "I've started working on mixing up the unit meta, which has been completely dominated by Light Tanks. As a first step, the movement of Light Tanks has been reduced from 6 to 5. At the same time, indirect vehicle movement has been increased to make them easier to reposition: Artillery 5 -> 6; Rockets 4 -> 5; Missiles 4 -> 5. I've also started designing two new direct vehicles to add more choices alongside the light tank.",
      },
    ],
  },
  {
    id: "2026-08-25",
    date: "2026-08-25",
    title: "AI improvements",
    items: [
      {
        category: "balance",
        text: "The AI will now try to move units away from a production building before attacking to stop it from baselocking itself as frequently.",
      },
    ],
  },
  {
    id: "2026-08-20",
    date: "2026-08-20",
    title: "AI build logic changes: take two",
    items: [
      {
        category: "balance",
        text: "Made further adjustments to the AI's build logic. I'll let this bake for a couple of days and see how the AI performs.",
      },
    ],
  },
  {
    id: "2026-08-19",
    date: "2026-08-19",
    title: "Fixed win condition bug",
    items: [
      {
        category: "bugfix",
        text: "Fixed a bug where the win condition for rout wasn't being properly checked on day two. If you manage to wipe out the enemy's army that early, it should count as a victory.",
      },
    ],
  },
  {
    id: "2026-08-16",
    date: "2026-08-16",
    title: "Added four additional themes",
    items: [
      {
        category: "feature",
        text: "Added four additional themes: Mesa, Swamp, Sakura, and Autumn. Also updated the mountains in most of the existing themes to make them more varied.",
      },
    ],
  },
  {
    id: "2026-08-11",
    date: "2026-08-11",
    title: "AI build logic changes",
    items: [
      {
        category: "balance",
        text: "The AI has studied the guide page more deeply and has made some adjustments to its build logic.",
      },
    ],
  },
  {
    id: "2026-08-11",
    date: "2026-08-11",
    title: "AI build logic changes",
    items: [
      {
        category: "balance",
        text: "The AI has studied the guide page more deeply and has made some adjustments to its build logic.",
      },
    ],
  },
  {
    id: "2026-08-10",
    date: "2026-08-10",
    title: "Re-worked Artillery and added range indicators",
    items: [
      {
        category: "balance",
        text: "Re-worked the Artillery unit. It can no longer move and shoot in the same turn. Instead, it now has a movement of 5 and a range of 2-3. To compensate, the Artillery has seen a damage increase across the board. The Sniper remains the only indirect fire unit that can move and shoot in the same turn. These changes will be applied starting from tomorrow at 00:00 UTC.",
      },
      {
        category: "feature",
        text: "Added range indicator UI for indirect fire units. When selecting and moving an indirect fire unit, range indicators will be shown to help visualize the unit's range.",
      },
    ],
  },
  {
    id: "2026-08-08",
    date: "2026-08-08",
    title: "Discord server is open",
    items: [
      {
        category: "feature",
        text: (
          <>
            The{" "}
            <a
              href={DISCORD_INVITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary underline underline-offset-4 hover:text-foreground"
            >
              Daily Wars Discord
            </a>{" "}
            is now available. After joining, you can link your Discord account on your profile to
            sync your display name and roles to the server. This is my first time creating and
            managing a Discord server, so please let me know if you have any feedback or
            suggestions.
          </>
        ),
      },
    ],
  },
  {
    id: "2026-08-07",
    date: "2026-08-07",
    title: "Achievements are live",
    items: [
      {
        category: "feature",
        text: "Added achievements. Thre's both milestone achievements such as career wins, and more unconventional ones such as successfully killing a Heavy Tank using a Rifleman. Recent achievements are featured on each player's profile, and you can also access the full list of achievements from there. Can you earn them all?",
      },
    ],
  },
  {
    id: "2026-08-05",
    date: "2026-08-05",
    title: "New victory conditions and map feature",
    items: [
      {
        category: "feature",
        text: "Added a new map feature: captureable monuments. Each owned monument increases an army's firepower by 10%.",
      },
      {
        category: "feature",
        text: "Maps can now have new victory conditions. Besides rout (defeat all enemy units), some maps now require you to capture a certain number of cities, while others require you to capture all monuments.",
      },
      {
        category: "feature",
        text: "Improved the tutorial a bit to better prepare new players for the daily map(s).",
      },
      {
        category: "bugfix",
        text: "Fixed some issues with rendering of roads.",
      },
    ],
  },
  {
    id: "2026-08-03.2",
    date: "2026-08-03",
    title: "Introducing medals and replays",
    items: [
      {
        category: "feature",
        text: "Added medals and a monthly and all-time ranking system. The top 3 scores on each daily leaderboard will be awarded a medal. If multiple players tie for the same score, they will all receive the same medal. For rankings, medals awarded are converted to points: Gold 5p, silver 3p, bronze 1p. Medals are counted from 2026-08-01 onwards.",
      },
      {
        category: "feature",
        text: "You can now watch replays of other players games. You can access replays from the leaderboard of a given map. Replays for a day are available after the leaderboard is closed, as to not spoil the current day's map, and are deleted after a week.",
      },
    ],
  },
  {
    id: "2026-08-01",
    date: "2026-08-01",
    title: "Streaks on the leaderboards",
    items: [
      {
        category: "feature",
        text: "Show off your daily streaks on the leaderboards. Players with streaks of 7 or more days gets a small flame icon next to their name.",
      },
    ],
  },
  {
    id: "2026-07-27",
    date: "2026-07-27",
    title: "Graphical Overhaul",
    items: [
      {
        category: "feature",
        text: "The game has been given a graphical overhaul with new art for units and terrain.",
      },
    ],
  },
  {
    id: "2026-07-19",
    date: "2026-07-19",
    title: "Enemy AI should be less suicidal.",
    items: [
      {
        category: "bugfix",
        text: "The AI should no longer lose a game by suiciding it's final unit to a counterattack, and should overall be more careful to not needlessly sacrifice units.",
      },
    ],
  },
  {
    id: "2026-07-18",
    date: "2026-07-18",
    title: "Statistics breakdown",
    items: [
      {
        category: "feature",
        text: (
          <>
            Added a{" "}
            <Link
              to="/stats"
              preload={false}
              className="font-medium text-primary underline underline-offset-4 hover:text-foreground"
            >
              stats page
            </Link>{" "}
            with charts showing units built and killed across all players.
          </>
        ),
      },
    ],
  },
  {
    id: "2026-07-17",
    date: "2026-07-17",
    title: "New production building",
    items: [
      {
        category: "feature",
        text: 'Added a new production building "Base". Bases can build both units from the Barracks and the Factory, and increases the capacity for both.',
      },
    ],
  },
  {
    id: "2026-07-16",
    date: "2026-07-16",
    title: "Capacity Increase and damage tweaks",
    items: [
      { category: "balance", text: "Production buildings now grants 5 capacity each, up from 4." },
      {
        category: "balance",
        text: "RPG damage vs. Battle Copter reduced from 40% to 30%.",
      },
      {
        category: "balance",
        text: "RPG damage vs. Transport Copter reduced from 60% to 50%.",
      },
      {
        category: "balance",
        text: "Light Tank damage vs. Battle Copter reduced from 40% to 30%.",
      },
      {
        category: "balance",
        text: "Light Tank damage vs. Transport Copter reduced from 60% to 50%.",
      },
    ],
  },
];

export function isChangelogEntryPublished(entry: ChangelogEntry, atMs = Date.now()): boolean {
  return entry.date <= utcCalendarDate(atMs);
}

export function getPublishedChangelogEntries(atMs = Date.now()): ChangelogEntry[] {
  return CHANGELOG_ENTRIES.filter((entry) => isChangelogEntryPublished(entry, atMs));
}

export function getLatestUpdate(atMs = Date.now()): ChangelogEntry | undefined {
  return getPublishedChangelogEntries(atMs)[0];
}

export const UPDATE_CATEGORY_LABELS: Record<UpdateCategory, string> = {
  feature: "Feature",
  balance: "Balance",
  bugfix: "Bug fix",
};
